import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "store";
import { activateGuide, deactivateGuide, markGuideCompleted, recordGuideEmission, restoreGuide, type ActiveGuide, type GuideSource, type PublishedGuidanceReport } from "store/guidanceSlice";
import { TECHPACK_CREATE_FLOW, type GuidedFlowDefinition } from "pages/TechPackPage/utils/techpackCreateFlow";
import type { TechPackGuidanceReport } from "pages/TechPackPage/utils/guidanceReport";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

/**
 * The guidance coordinator (REF-6217 T1.3) — the whole guided-workflow runtime.
 *
 * It does exactly three things: own **one** active-guide record, join that record to the authored
 * definition (T1.2) and the feature's published report (T1.1), and emit the funnel's analytics. It
 * is not an engine: it computes no step, no readiness, no next-step rule, and holds no product
 * state. Every question about *where the user is* is answered by the feature, every time.
 *
 * The rule that keeps it honest: **the coordinator never writes anything it could read.** The step,
 * its readiness and its blocked reason are read from `guidance.published`, which Tech Pack's
 * provider owns; the record here carries only identity, provenance and start time.
 *
 * **Nothing mounts this yet, and that is deliberate — it is not dead code.** T1.4's guide card is
 * its first consumer (it needs the same `guide` view it renders from) and T1.5's `?guide=` handler
 * is the first caller of `start`. Mounting it earlier would run an effect on every authorized page
 * for a runtime no user can reach.
 */

/**
 * The single supported flow. A map rather than a registry: adding Phase 2's second flow is one more
 * entry, and `start(flowId, …)` already takes the id as an argument, so no caller changes shape.
 */
const FLOWS: Record<string, GuidedFlowDefinition> = { [TECHPACK_CREATE_FLOW.id]: TECHPACK_CREATE_FLOW };

export const findGuidedFlow = (flowId: string): GuidedFlowDefinition | null => FLOWS[flowId] ?? null;

/**
 * Mirrors the active record for a same-tab refresh (plan §8). `sessionStorage`, so a new tab starts
 * unguided by construction — cross-device and cross-tab persistence are deliberately out of scope.
 * Only the record is stored; nothing about the feature's async work is, because Tech Pack already
 * re-arms its own extraction monitoring on reload and the guide reads the result.
 */
const STORAGE_KEY = "guide_active_flow";

/**
 * **Invariant: every dispatch that mutates the record is followed by a persist of the LIVE record**
 * (`persistGuide(store.getState().guidance.active)`), never a closure snapshot.
 *
 * Missing that is not a cosmetic staleness — it corrupts the funnel. The record used to be written
 * once, at `start`, so a completed run still read `status: "active"` on disk: a refresh restored it
 * as running, the finished project republished `done`, and `guide_completed` fired a second time
 * (with `duration_ms` inflated by however long the tab had been idle), once more on every further
 * refresh. A stale `lastEmittedStep: null` re-emitted `guide_step_shown` the same way.
 */
const persistGuide = (record: ActiveGuide | null) => {
  try {
    if (record) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode, quota, or a blocked storage partition. The guide then simply does not survive a
    // refresh — never a reason to break the page.
  }
};

/** Reads a persisted record back, discarding anything a current definition doesn't recognise. */
const readPersistedGuide = (): ActiveGuide | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as ActiveGuide;
    const definition = findGuidedFlow(parsed?.flowId);
    // Fail safe, never guess: an unknown flow or a record written by an older definition is
    // dropped rather than restored onto semantics that may have moved under it.
    if (!definition || parsed.version !== definition.version || typeof parsed.startedAt !== "number") {
      persistGuide(null);

      return null;
    }

    return parsed;
  } catch {
    persistGuide(null);

    return null;
  }
};

/**
 * What a renderer consumes. One coherent object, so the guide UI never joins the slice and the
 * definition itself.
 *
 * `away` means the guide is running but its feature isn't mounted — the user navigated off. It is
 * emphatically not an exit: the record survives, and the definition's `entry` is right there for a
 * "return" affordance.
 */
export type GuideView =
  | { status: "inactive" }
  | { status: "active"; definition: GuidedFlowDefinition; source: GuideSource; report: TechPackGuidanceReport }
  | { status: "away"; definition: GuidedFlowDefinition; source: GuideSource }
  /** Terminal. `report` is null if the user had already navigated away when it finished. */
  | { status: "completed"; definition: GuidedFlowDefinition; source: GuideSource; report: TechPackGuidanceReport | null };

/** The report for this guide's own feature, or null — the away test, in one place. */
const reportFor = (definition: GuidedFlowDefinition, published: PublishedGuidanceReport | null): TechPackGuidanceReport | null =>
  published && published.feature === definition.capabilityId ? published.report : null;

export const selectGuideView = (active: ActiveGuide | null, published: PublishedGuidanceReport | null): GuideView => {
  if (!active) return { status: "inactive" };

  const definition = findGuidedFlow(active.flowId);
  // A record naming a flow this build no longer has: show nothing rather than a half-guide.
  if (!definition) return { status: "inactive" };

  const report = reportFor(definition, published);
  const common = { definition, source: active.source };

  if (active.status === "completed") return { status: "completed", ...common, report };

  return report ? { status: "active", ...common, report } : { status: "away", ...common };
};

export const useGuideCoordinator = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const active = useAppSelector((s) => s.guidance.active);
  const published = useAppSelector((s) => s.guidance.published);

  /**
   * Begin (or re-affirm) a guided run. This is the entire public entry point, and the exact call
   * Phase 2's chatbot handler makes — `start(flowId, { source: "chatbot" })`, after
   * `confirmNavigation()` resolves true and before navigating to `definition.entry`.
   *
   * Returns false only for a flow id this build doesn't know, so a bad `?guide=` value can fall
   * back to plain navigation.
   */
  const start = useCallback(
    (flowId: string, options: { source: GuideSource }): boolean => {
      const definition = findGuidedFlow(flowId);
      if (!definition) return false;

      // Live state, NOT the `active` snapshot above. StrictMode invokes an effect's body twice in
      // the same commit, so a caller in an effect calls this twice while the snapshot still reads
      // "nothing active" — emitting `guide_started` twice and (before the reducer's own guard)
      // resetting the run. The same read makes two mounted consumers safe, since dispatch is
      // synchronous and the second one sees the first's write.
      const current = store.getState().guidance.active;
      if (current && current.flowId === flowId && current.status === "active") return true;

      dispatch(activateGuide({ flowId, version: definition.version, source: options.source, startedAt: Date.now() }));
      persistGuide(store.getState().guidance.active);
      trackEvent(EVENTS.GUIDE.STARTED, { flow: flowId, source: options.source });

      return true;
    },
    [dispatch, store]
  );

  /**
   * The user dismissed the guide. Deliberately narrow: it does not navigate, does not touch Tech
   * Pack's state or its wizard, and does not clear the conversation — after this the app behaves
   * exactly as it does unguided.
   *
   * Dismissing an already-*completed* run emits nothing: that run already ended with
   * `guide_completed`, and counting it as an exit too would double-count the funnel's endings.
   */
  const exit = useCallback(() => {
    const state = store.getState().guidance;
    const current = state.active;
    if (!current) return;

    const definition = findGuidedFlow(current.flowId);
    const wasRunning = current.status === "active";
    const step = definition ? reportFor(definition, state.published)?.step : undefined;

    dispatch(deactivateGuide());
    persistGuide(null);
    if (wasRunning) trackEvent(EVENTS.GUIDE.EXITED, { flow: current.flowId, step: step ?? "away" });
  }, [dispatch, store]);

  // Adopt a run this tab started before a refresh. Guarded on "nothing active" rather than a
  // mounted-once flag, which is self-limiting: after `exit` the storage is empty, so a later
  // remount finds nothing to restore.
  useEffect(() => {
    if (store.getState().guidance.active) return;
    const restored = readPersistedGuide();
    if (restored) dispatch(restoreGuide(restored));
  }, [dispatch, store]);

  /**
   * The only place the coordinator reacts to the feature: on every change to either half of the
   * guidance state, reconcile the record against the report.
   *
   * The body reads live store state instead of the closure's `active` / `published` — those are in
   * the dep list to *trigger* the pass, but a second pass in the same commit (StrictMode, or a
   * second mounted consumer) must see what the first one already recorded, or every event here
   * fires twice.
   */
  useEffect(() => {
    const state = store.getState().guidance;
    const current = state.active;
    if (!current || current.status !== "active") return;

    const definition = findGuidedFlow(current.flowId);
    if (!definition) return;

    const report = reportFor(definition, state.published);
    // Away: the feature isn't mounted, so there is nothing to report and nothing to forget. Leaving
    // the bookkeeping intact is what stops a navigate-away-and-back from re-emitting the same step.
    if (!report) return;

    // Completion is feature truth and nothing else: the terminal step the feature publishes when
    // `techpackInfo` exists. Never `stepReady`, which is true on every step the user can leave.
    if (report.step === "done") {
      dispatch(markGuideCompleted());
      // Persist the terminal status, or a refresh restores this run as still-active and completes
      // it all over again. `selectGuideView` renders a restored `completed` record as completed and
      // this effect bails on it above, so the run ends exactly once however many times the tab
      // reloads.
      persistGuide(store.getState().guidance.active);
      trackEvent(EVENTS.GUIDE.COMPLETED, { flow: current.flowId, duration_ms: Date.now() - current.startedAt });

      return;
    }

    const stepChanged = report.step !== current.lastEmittedStep;
    const reasonChanged = report.blockedReason !== current.lastEmittedBlockedReason;
    if (!stepChanged && !reasonChanged) return;

    dispatch(recordGuideEmission({ step: report.step, blockedReason: report.blockedReason }));
    // Same reason as completion: the bookkeeping only dedupes across a refresh if the refresh can
    // read it back.
    persistGuide(store.getState().guidance.active);
    if (stepChanged) trackEvent(EVENTS.GUIDE.STEP_SHOWN, { flow: current.flowId, step: report.step });
    // Edge-triggered: only a *newly* blocked reason. A reason that merely persists across renders
    // doesn't re-emit. A stall the user clears and then hits again DOES emit twice — deliberately:
    // the intervening ready state records `null`, so the second block is a second real stall.
    if (report.blockedReason && reasonChanged) trackEvent(EVENTS.GUIDE.BLOCKED, { flow: current.flowId, step: report.step, reason: report.blockedReason });
  }, [dispatch, store, active, published]);

  const guide = useMemo(() => selectGuideView(active, published), [active, published]);

  return { guide, start, exit };
};
